import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { FlexContainer, FroalaEditor } from '@edulastic/common'
import styled from 'styled-components'
import { Rnd } from 'react-rnd'
import { IconClose } from '@edulastic/icons'
import ResizeIconNotes from '@edulastic/icons/src/ResizeIconNotes'
import i18n from '@edulastic/localization'
import { red } from '@edulastic/colors'
import {
  updateNoteAction,
  getNoteContentSelector,
} from '../../sharedDucks/notes'
import { updateTestPlayerAction } from '../../../author/sharedDucks/testPlayer'
import { themes } from '../../../theme'

// Character limit for notes
const NOTES_CHAR_LIMIT = 1000
// Helper function to count characters in HTML content (strips HTML tags)
const getCharacterCount = (htmlContent) => {
  if (!htmlContent) return 0
  // Create a temporary div to strip HTML tags
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  return tempDiv.textContent?.length || 0
}

const Notes = ({
  noteContent = '',
  isNoteVisible,
  updateTestPlayer,
  onChangeNoteContent,
  itemId,
}) => {
  const {
    background,
    devider,
    borderColor,
  } = themes.playerSkin.quester.notesModal

  // State for modal position and size
  const [position, setPosition] = useState({ x: 100, y: 100 })
  const [size, setSize] = useState({ width: 500, height: 400 })
  const [visibleCharacterCount, setVisibleCharacterCount] = useState(
    getCharacterCount(noteContent)
  )

  const closeNote = useCallback(() => {
    updateTestPlayer({ isNoteVisible: false })
  }, [updateTestPlayer])

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        closeNote()
      }
    },
    [closeNote]
  )

  const handleDragStop = useCallback((e, d) => {
    setPosition({ x: d.x, y: d.y })
  }, [])

  const handleResizeStop = useCallback(
    (e, direction, ref, delta, newPosition) => {
      setSize({
        width: ref.style.width.replace('px', ''),
        height: ref.style.height.replace('px', ''),
      })
      setPosition({ x: newPosition.x, y: newPosition.y })
    },
    []
  )

  // Handle note content change with character limit restriction
  const handleNoteContentChange = useCallback(
    (content) => {
      const newCharacterCount = getCharacterCount(content)
      setVisibleCharacterCount(newCharacterCount)
      // Only allow the change if it doesn't exceed the character limit
      if (newCharacterCount <= NOTES_CHAR_LIMIT) {
        const contentToSave = newCharacterCount > 0 ? content : ''
        onChangeNoteContent({ itemId, content: contentToSave })
      }
    },
    [itemId, onChangeNoteContent]
  )

  if (!isNoteVisible) return null

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <Rnd
        size={size}
        position={position}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        minWidth={300}
        minHeight={200}
        maxWidth={1000}
        maxHeight={1000}
        dragHandleClassName="modal-header"
        bounds="window"
      >
        <StyledModal destroyOnClose borderColor={borderColor}>
          <ModalHeader
            className="modal-header"
            background={background}
            devider={devider}
          >
            <ModalTitle>{i18n.t('header:toolbar.notes')}</ModalTitle>
            <CloseButton onClick={closeNote}>
              <IconClose color={devider} />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            <NotesContainer
              background={background}
              color={devider}
              devider={devider}
              inputHeight={size?.height}
            >
              <FroalaEditor
                data-cy="templateBox"
                value={noteContent}
                onChange={handleNoteContentChange}
                border="border"
                toolbarInline={false}
                toolbarSticky={false}
                initOnClick={false}
                quickInsertTags={[]}
                buttons={['undo', 'redo', '|', 'bold', 'italic', 'underline']}
                toolbarSize="SM"
                unsetMaxWidth
                sanitizeClipboardHtml
                restrictTags
              />
            </NotesContainer>
            <FlexContainer justifyContent="space-between" alignItems="center">
              <CharacterCountDisplay
                isNearLimit={visibleCharacterCount > NOTES_CHAR_LIMIT}
              >
                {i18n.t('header:toolbar.characters')}: {visibleCharacterCount}/
                {NOTES_CHAR_LIMIT}
              </CharacterCountDisplay>
              <StyledIconWrapper>
                <ResizeIconNotes />
              </StyledIconWrapper>
            </FlexContainer>
          </ModalBody>
        </StyledModal>
      </Rnd>
    </ModalOverlay>
  )
}

Notes.propTypes = {
  noteContent: PropTypes.string,

  onChangeNoteContent: PropTypes.func,
}

Notes.defaultProps = {
  noteContent: '',
  onChangeNoteContent: () => {},
}

const mapStateToProps = (state, { itemId = '' }) => ({
  noteContent: getNoteContentSelector(state)(itemId),
  isNoteVisible: state.testPlayer.isNoteVisible || false,
})

const mapDispatchToProps = {
  updateTestPlayer: updateTestPlayerAction,
  onChangeNoteContent: updateNoteAction,
}

const enhance = compose(connect(mapStateToProps, mapDispatchToProps))

export default enhance(Notes)

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  pointer-events: auto;

  > div {
    pointer-events: auto;
  }
`

const StyledModal = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid ${({ borderColor }) => borderColor};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ devider }) => devider};
  background: ${({ background }) => background};
  cursor: move;
  user-select: none;
`

const ModalTitle = styled.h3`
  color: #fff;
  text-align: center;
  font-family: 'Open Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  margin: 0;
  color: ${({ color }) => color};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  padding: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;

  &:hover {
    color: rgba(0, 0, 0, 0.75);
    background: rgba(0, 0, 0, 0.06);
  }
`

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const NotesContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .fr-toolbar.fr-top,
  .fr-box.fr-basic .fr-wrapper {
    border: none;
  }

  .fr-box {
    display: flex;
    flex-direction: column;
    border: none;
    flex: 1;
  }

  .fr-wrapper {
    flex: 1;
    overflow: auto;
  }

  .second-toolbar {
    display: none;
  }
  .fr-toolbar .fr-newline {
    margin: 0;
  }
  .fr-toolbar.fr-top {
    border-radius: unset;
  }
  .fr-separator.fr-vs {
    background: ${({ devider }) => devider};
  }
  .fr-toolbar.fr-desktop.fr-top.fr-basic {
    background: ${({ background }) => background};
  }
  .fr-toolbar .fr-command.fr-btn svg path,
  .fr-popup .fr-command.fr-btn svg path,
  .fr-modal .fr-command.fr-btn svg path {
    fill: ${({ color }) => color};
  }
  .fr-desktop .fr-command:hover:not(.fr-table-cell) {
    background: ${themes.playerSkin.quester.footer.hover.background};
  }
  .fr-desktop .fr-command:active {
    color: ${({ color }) => color};
    background: ${themes.playerSkin.quester.footer.hover.background};
  }
  .fr-command.fr-btn.fr-active {
    background: ${themes.playerSkin.quester.footer.hover.background};
  }

  .fr-toolbar
    .fr-btn-grp
    .fr-command.fr-btn.fr-active:not(.fr-dropdown)
    svg
    path {
    fill: ${({ color }) => color};
  }

  .fr-box.fr-basic .fr-wrapper {
    max-height: ${({ inputHeight }) => inputHeight - 139}px;
  }
`

const CharacterCountDisplay = styled.div`
  font-size: 12px;
  color: ${({ isNearLimit }) => (isNearLimit ? red : '#666')};
  text-align: left;
  padding: 8px 16px;
  background: transparent;
  flex-shrink: 0;
  font-weight: ${({ isNearLimit }) => (isNearLimit ? '600' : 'normal')};
  transition: color 0.2s ease;
`

const StyledIconWrapper = styled.div`
  position: absolute;
  bottom: -4px;
  right: 1px;
`
